<template>
  <div class="absolute">
    <slot></slot>
    <teleport to="#teleport-connection">
      <span v-if="status === 'OPEN'" class="flex items-center"
        ><SocketConnected
          class="h-3 w-3 text-green-600 mr-1 MM-socket-connected__status"
        ></SocketConnected
        >{{ $t("Connected") }}</span
      >
      <AppTooltip v-else-if="status === 'CLOSED'" size="sm" position="top">
        <template #trigger>
          <span class="flex items-center" @click="open"
            ><SocketError
              class="h-3 w-3 text-red-600 rotate-45 mr-1 MM-socket-disconnected__status"
            ></SocketError
            >{{ $t("Disconnected") }}</span
          >
        </template>
        <span>{{ $t("SocketDisconnectTooltip") }}</span>
      </AppTooltip>
      <span v-else-if="status === 'CONNECTING'" class="flex items-center"
        ><SocketConnecting
          class="h-3 w-3 text-yellow-400 dark:text-yellow-500 mr-1"
        ></SocketConnecting
        >{{ $t("Connecting") }}...</span
      >
    </teleport>
    <!-- Notifications -->
    <JobNotificationList
      v-if="jobsStore.isNotificationPanelOpen"
      class=""
      @close="jobsStore.closeNotificationPanel()"
    />
  </div>
</template>

<script setup lang="ts">
import SocketConnected from "@/components/icons/SocketConnected.vue";
import SocketConnecting from "@/components/icons/SocketConnecting.vue";
import SocketError from "@/components/icons/SocketError.vue";
import AppTooltip from "@/components/shared/AppTooltip.vue";
import type {
  OngoingJob,
  TaskJobData,
  SocketData,
  AssistantJobSocketData,
  ConversationJobData,
  FileProcessedJobData,
  EmailJobData,
  SocialMediaPostStatusData,
} from "@/types/jobs";
import JobNotificationList from "@/components/jobs/JobNotificationList.vue";
import { useInitialBulkTaskState } from "@/composables/initialBulkTask";
import { handleSocialMediaPostStatus } from "@/composables/socialMediaPostWebsocket";
import { useToast } from "vue-toast-notification";
import { defaultToastOptions } from "@/utils/consts";
const { addEmailProcessingJob, addFileUploadJob } = useInboxWebsocketJobs();

const { t } = withI18nHelpers();
const $toast = useToast();
const queryClient = useQueryClient();
const { mockSocketUrl } = useMockSocketServer();
const route = useRoute();
const session = currentSessionInformation;
const jobsStore = useJobsStore();
const connectionUrl = computed(() => {
  if (USE_MOCK_SOCKET) return mockSocketUrl;
  return session?.appWebsocketApiUrl
    ? `${session?.appWebsocketApiUrl}?authorizationToken=${session?.appAuthToken}`
    : undefined;
});
const { maybeAddSubtaskEvent, maybeAddParentTaskEvent } = useInitialBulkTaskState();
const { handleV2ConversationMessageEvent } = useV2ConversationSocketHandler();

const fileRefetchStatuses: FileProcessedJobData["status"][] = [
  "uploaded",
  "processing",
  "processed",
];

const { status, open } = useWebSocket(connectionUrl, {
  autoReconnect: {
    retries: 3,
    delay: 3000,
    onFailed() {
      console.error("WebSocket connection failed after 3 retries.");
    },
  },
  heartbeat: {
    message: "",
    interval: 30_000,
  },
  onMessage(_, event) {
    onRefresh(event.data);
  },
});

function onRefresh(message: string) {
  const parseMessage: SocketData = maybeParseJson(message);
  const { action, data: _data } = parseMessage;

  switch (action) {
    case "NewTask":
      maybeAddParentTaskEvent(_data as TaskJobData);
      break;
    case "UserUpdated":
    case "AccountUpdated":
      queryClient.invalidateQueries({ queryKey: ["account"], exact: true });
      break;
    case "TaskUpdate":
      const data = _data as TaskJobData;
      const queryKey = ["task", { id: data.taskId }];
      updateItemStatusInTaskListQueryCache(queryClient, data.taskId, data.currentStatus);
      queryClient.invalidateQueries({ queryKey });
      maybeAddSubtaskEvent(data);
      //Mutate task list
      updateItemStatusInTaskListQueryCache(queryClient, data.taskId, data.currentStatus);

      // Skip job notifications for social media posts - they're handled via SocialMediaPostStatus events
      const isSocialMediaPost =
        data.taskType?.includes("post-to-") || data.taskType?.startsWith("social-media/");

      if (data.currentStatus === "complete") {
        // Handle job notifications for non-social media posts
        if (
          (route.name !== "single-task" || route.params.id !== data.taskId) &&
          !isSocialMediaPost
        ) {
          jobsStore.handleJobEvent(formatTaskCompleteJob(data.taskId));
        }
      }
      break;
    case "FileUploadStatus":
      if ((_data as FileProcessedJobData).fileId) {
        addFileUploadJob(_data as FileProcessedJobData);
        if ("status" in _data && _data.status === "processed") {
          prefetchSingleFileById((_data as FileProcessedJobData).fileId, queryClient);
        }
      }
      if (fileRefetchStatuses.includes((_data as FileProcessedJobData).status)) {
        queryClient.invalidateQueries({
          queryKey: ["files", "list"],
        });
      }
      break;
    case "NotificationIntentStatus":
      addEmailProcessingJob(_data as EmailJobData);
      break;
    case "AssistantMessage":
      handleV2ConversationMessageEvent(_data as ConversationJobData);
      break;
    case "JobProcessingUpdate":
      maybeShowJobProcessingNotification(_data as AssistantJobSocketData);
      break;
    case "JobProcessingComplete":
      maybeShowJobProcessingNotification(_data as AssistantJobSocketData);
      if ((_data as AssistantJobSocketData).jobType === "AccountDataRetryWebsite") {
        // clean up retry status
        handleRetryCompletion(_data as AssistantJobSocketData, queryClient);
      }
      if (
        [
          "account_discover_website",
          "process_website",
          "account_discover_competitor",
          "ProcessSocialMedia",
        ].includes((_data as AssistantJobSocketData).jobType)
      ) {
        const data = _data as AssistantJobSocketData;
        handleDiscoveryJob(data, queryClient);
      }
      break;
    case "SocialMediaPostStatus":
      // console.log("[SocketContext] SocialMediaPostStatus event received:", _data);
      const socialMediaData = _data as SocialMediaPostStatusData;
      handleSocialMediaPostStatus(socialMediaData);

      if (socialMediaData.status === "completed") {
        const platformName =
          socialMediaData.platform.charAt(0).toUpperCase() +
          socialMediaData.platform.slice(1);
        $toast.success(t(`posted${platformName}`), defaultToastOptions);
      } else if (socialMediaData.status === "error") {
        $toast.error(socialMediaData.error || t("errorPosting"), defaultToastOptions);
      }
      break;
    default:
      break;
  }
}

function maybeShowJobProcessingNotification(job: AssistantJobSocketData) {
  // Skip job notifications for social media posts
  if (job?.jobType?.includes("post-to-") || job?.jobType?.startsWith("social-media/")) {
    return;
  }

  if (
    [
      "process_website",
      "crawl_website",
      "crawl_website_page",
      "crawl_website",
      "ProcessSocialMedia",
    ].includes(job?.jobType)
  ) {
    // @ts-ignore -- job is not the correct type
    const data = job as OngoingJob;
    // crawl complete
    if (job.status === "complete" && job.jobType === "crawl_website") {
      jobsStore.handleJobEvent(formatWebsiteCrawlComplete(job));
      return;
    }
    // ongoing
    if (!data.data?.total) return;
    jobsStore.handleJobEvent({
      ...data,
      title: t("processingPages"),
    });
  }
}
</script>
